import { useState, useEffect, useRef } from 'react';
import { getQuote, updateQuote, sendQuote, type QuoteDetail } from '@/lib/api/quotes';
import apiClient from '@/lib/api/client';
import { PrintableQuote } from '@/components/estimates/PrintableQuote';
import { printQuote } from '@/lib/utils/generateInvoicePdf';
import { getSettings } from '@/lib/api/settings';

type Section = {
  id: string;
  title: string;
  items: Array<{
    id: string;
    name: string;
    description: string;
    quantity: number;
    unitPrice: number;
    unit: string;
  }>;
};

type QuoteBuilderModalProps = {
  quoteId: string;
  isOpen: boolean;
  onClose: () => void;
  onSaved?: () => void;
};

export const QuoteBuilderModal = ({ quoteId, isOpen, onClose, onSaved }: QuoteBuilderModalProps) => {
  const printableRef = useRef<HTMLDivElement>(null);
  const [quoteData, setQuoteData] = useState<QuoteDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [sections, setSections] = useState<Section[]>([]);
  const [taxRate, setTaxRate] = useState(5); // BC: GST only, matches invoicing
  const [companyInfo, setCompanyInfo] = useState<{
    name: string;
    address: string;
    phone: string;
    email: string;
    logo?: string;
  } | undefined>(undefined);

  // Fetch company settings for printing
  useEffect(() => {
    if (isOpen) {
      getSettings()
        .then((settings) => {
          if (settings.company) {
            const c = settings.company;
            const addressParts = [
              c.address,
              c.city && c.state ? `${c.city}, ${c.state}` : c.city || c.state,
              c.zipCode,
            ].filter(Boolean);

            setCompanyInfo({
              name: c.companyName || 'OchrePoint Contracting',
              address: addressParts.join('\n'),
              phone: c.phone || '',
              email: c.email || '',
              logo: c.logoUrl,
            });
          }
        })
        .catch((err) => {
          console.error('Failed to load company settings:', err);
        });
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && quoteId) {
      loadQuote();
    }
  }, [isOpen, quoteId]);

  const loadQuote = async () => {
    try {
      setLoading(true);
      const data = await getQuote(quoteId);
      setQuoteData(data);
      // 0 is a real stored rate, not "unset"
      if ((data as any).tax_rate != null) setTaxRate(Number((data as any).tax_rate));
      
      if (data.sections && data.sections.length > 0) {
        const transformedSections = data.sections.map((section: any) => ({
          id: section.id.toString(),
          title: section.name || section.title,
          items: data.items
            .filter((item: any) => item.section_id === section.id.toString() || item.section_id === section.id)
            .map((item: any) => ({
              id: item.id.toString(),
              name: item.item_name || item.name,
              description: item.description || '',
              quantity: item.quantity,
              unitPrice: item.unit_price,
              unit: item.unit || 'ea',
            })),
        }));
        setSections(transformedSections);
      } else {
        setSections([]);
      }
    } catch (error) {
      console.error('Failed to load quote:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Save sections and items to backend
      const sectionsData = sections.map((section, sectionIndex) => ({
        id: section.id.startsWith('temp-') ? undefined : parseInt(section.id),
        name: section.title,
        display_order: sectionIndex,
        items: section.items.map((item, itemIndex) => ({
          id: item.id.startsWith('temp-') ? undefined : parseInt(item.id),
          item_name: item.name,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          unit: item.unit,
          display_order: itemIndex,
        })),
      }));

      await apiClient.put(`/quotes/${quoteId}/sections`, { sections: sectionsData, tax_rate: taxRate });
      await updateQuote(quoteId, { status: 'draft' });
      
      onSaved?.();
      onClose();
    } catch (error) {
      console.error('Failed to save quote:', error);
      alert('Failed to save quote');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Are you sure you want to delete this quote? This action cannot be undone.')) {
      return;
    }
    
    try {
      setDeleting(true);
      await apiClient.delete(`/quotes/${quoteId}`);
      onSaved?.();
      onClose();
    } catch (error) {
      console.error('Failed to delete quote:', error);
      alert('Failed to delete quote');
      setDeleting(false);
    }
  };

  const handleSend = async () => {
    if (!confirm('Send this quote to the client?')) return;
    
    try {
      setSaving(true);
      await sendQuote(quoteId);
      await updateQuote(quoteId, { status: 'sent' });
      onSaved?.();
      onClose();
    } catch (error) {
      console.error('Failed to send quote:', error);
      alert('Failed to send quote');
    } finally {
      setSaving(false);
    }
  };

  const handlePrint = () => {
    if (!printableRef.current) return;
    printQuote(printableRef.current);
  };

  const handleAddSection = () => {
    const sectionName = prompt('Enter section name:');
    if (!sectionName) return;

    setSections([...sections, {
      id: `temp-${Date.now()}`,
      title: sectionName,
      items: [],
    }]);
  };

  const handleDeleteSection = (sectionId: string) => {
    if (!confirm('Delete this section and all its items?')) return;
    setSections(sections.filter((s) => s.id !== sectionId));
  };

  const handleAddLineItem = (sectionId: string) => {
    setSections(sections.map((section) =>
      section.id === sectionId
        ? { ...section, items: [...section.items, {
            id: `temp-item-${Date.now()}`,
            name: '',
            description: '',
            quantity: 1,
            unitPrice: 0,
            unit: 'ea',
          }] }
        : section
    ));
  };

  const handleDeleteLineItem = (sectionId: string, itemId: string) => {
    setSections(sections.map((section) =>
      section.id === sectionId
        ? { ...section, items: section.items.filter((item) => item.id !== itemId) }
        : section
    ));
  };

  const handleUpdateLineItem = (sectionId: string, itemId: string, field: string, value: string | number) => {
    setSections(sections.map((section) =>
      section.id === sectionId
        ? {
            ...section,
            items: section.items.map((item) =>
              item.id === itemId ? { ...item, [field]: value } : item
            ),
          }
        : section
    ));
  };

  const calculateSubtotal = () => {
    return sections.reduce((total, section) => {
      const sectionTotal = section.items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
      return total + sectionTotal;
    }, 0);
  };

  const subtotal = calculateSubtotal();
  const tax = (subtotal * taxRate) / 100;
  const grandTotal = subtotal + tax;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(value);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Hidden Printable Area */}
        <div style={{ position: 'absolute', left: '-9999px', top: 0, backgroundColor: '#ffffff' }}>
          {quoteData && (
            <PrintableQuote 
              ref={printableRef} 
              quote={quoteData} 
              companyInfo={companyInfo} 
            />
          )}
        </div>

        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              Quote Builder
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {quoteData ? `Quote #${(quoteData as any).quote_number || quoteId}` : 'Loading...'}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handlePrint}
              disabled={loading || !quoteData}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-base">print</span>
              Print
            </button>
            <button
              onClick={handleSave}
              disabled={saving || loading}
              className="px-4 py-2 bg-gray-100 text-gray-900 rounded-lg hover:bg-gray-200 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Draft'}
            </button>
            <button
              onClick={handleSend}
              disabled={saving || loading || deleting}
              className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:opacity-90 disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-base">send</span>
              Send Quote
            </button>
            <button
              onClick={handleDelete}
              disabled={saving || loading || deleting}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              <span className="material-symbols-outlined text-base">delete</span>
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <p className="mt-2 text-gray-600">Loading quote...</p>
              </div>
            </div>
          ) : (
            <div className="flex gap-6">
              {/* Main Content */}
              <div className="flex-1 space-y-6">
                {/* Quote Info */}
                {quoteData && (
                  <div className="bg-gray-50 rounded-xl p-4 grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm text-gray-600">Job</p>
                      <p className="font-semibold text-gray-900">{(quoteData as any).job_title || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Client</p>
                      <p className="font-semibold text-gray-900">{(quoteData as any).client_name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Status</p>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        (quoteData as any).status === 'Approved' ? 'bg-green-100 text-green-800' :
                        (quoteData as any).status === 'Sent' ? 'bg-blue-100 text-blue-800' :
                        (quoteData as any).status === 'Draft' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {(quoteData as any).status}
                      </span>
                    </div>
                  </div>
                )}

                {/* Sections */}
                {sections.map((section) => (
                  <div key={section.id} className="bg-white rounded-xl border border-gray-200">
                    <div className="p-4 border-b border-gray-200 flex items-center justify-between bg-gray-50 rounded-t-xl">
                      <h3 className="font-bold text-gray-900">{section.title}</h3>
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleAddLineItem(section.id)}
                          className="text-sm text-primary hover:underline"
                        >
                          + Add Item
                        </button>
                        <button
                          onClick={() => handleDeleteSection(section.id)}
                          className="text-sm text-red-600 hover:underline"
                        >
                          Delete Section
                        </button>
                      </div>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="text-xs uppercase text-gray-600 bg-gray-50">
                          <tr>
                            <th className="px-4 py-2 text-left">Item</th>
                            <th className="px-4 py-2 text-right w-24">Qty</th>
                            <th className="px-4 py-2 text-right w-32">Unit Price</th>
                            <th className="px-4 py-2 text-right w-32">Total</th>
                            <th className="px-4 py-2 w-16"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {section.items.length === 0 ? (
                            <tr>
                              <td colSpan={5} className="px-4 py-4 text-center text-gray-500">
                                No items. Click "+ Add Item" to add.
                              </td>
                            </tr>
                          ) : (
                            section.items.map((item) => (
                              <tr key={item.id} className="border-b border-gray-100">
                                <td className="px-4 py-2">
                                  <input
                                    type="text"
                                    value={item.name}
                                    onChange={(e) => handleUpdateLineItem(section.id, item.id, 'name', e.target.value)}
                                    placeholder="Item name"
                                    className="w-full px-2 py-1 border border-gray-200 rounded focus:ring-2 focus:ring-primary focus:border-primary"
                                  />
                                </td>
                                <td className="px-4 py-2">
                                  <input
                                    type="number"
                                    value={item.quantity}
                                    onChange={(e) => handleUpdateLineItem(section.id, item.id, 'quantity', Number(e.target.value))}
                                    className="w-full px-2 py-1 border border-gray-200 rounded text-right focus:ring-2 focus:ring-primary focus:border-primary"
                                  />
                                </td>
                                <td className="px-4 py-2">
                                  <input
                                    type="number"
                                    step="0.01"
                                    value={item.unitPrice}
                                    onChange={(e) => handleUpdateLineItem(section.id, item.id, 'unitPrice', Number(e.target.value))}
                                    className="w-full px-2 py-1 border border-gray-200 rounded text-right focus:ring-2 focus:ring-primary focus:border-primary"
                                  />
                                </td>
                                <td className="px-4 py-2 text-right font-semibold">
                                  {formatCurrency(item.quantity * item.unitPrice)}
                                </td>
                                <td className="px-4 py-2 text-center">
                                  <button
                                    onClick={() => handleDeleteLineItem(section.id, item.id)}
                                    className="text-red-600 hover:text-red-800"
                                  >
                                    <span className="material-symbols-outlined text-base">delete</span>
                                  </button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ))}

                {/* Add Section Button */}
                <button
                  onClick={handleAddSection}
                  className="w-full py-3 border-2 border-dashed border-gray-300 rounded-xl text-gray-600 hover:border-primary hover:text-primary transition-colors"
                >
                  + Add Section
                </button>
              </div>

              {/* Summary Sidebar */}
              <div className="w-72 shrink-0">
                <div className="bg-gray-50 rounded-xl p-4 sticky top-0 space-y-4">
                  <h3 className="font-bold text-gray-900">Quote Summary</h3>
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-semibold">{formatCurrency(subtotal)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Tax ({taxRate}%)</span>
                      <span className="font-semibold">{formatCurrency(tax)}</span>
                    </div>
                    <div className="border-t border-gray-200 pt-2 flex justify-between">
                      <span className="font-bold text-gray-900">Total</span>
                      <span className="font-bold text-xl text-primary">{formatCurrency(grandTotal)}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">
                      Tax Rate (%)
                    </label>
                    <input
                      type="number"
                      value={taxRate}
                      onChange={(e) => setTaxRate(Number(e.target.value))}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary"
                      step="0.1"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
